import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getIntendedPath = (): string => {
    const state = location.state as { from?: { pathname: string; search?: string; hash?: string } } | null;
    const from = state?.from;
    if (from?.pathname) {
      return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
    }
    const urlFrom = new URLSearchParams(location.search).get('from');
    return urlFrom || '/dashboard';
  };

  const handleGoogleLogin = () => {
    const intended = getIntendedPath();
    localStorage.setItem('postLoginRedirect', intended);
    const base = `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}`;
    const url = `${base}/api/v1/auth/google?returnTo=${encodeURIComponent(intended)}`;
    window.location.href = url;
  };

  useEffect(() => {
    const check = async () => {
      try {
        await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/v1/auth/me`, {
          withCredentials: true,
        });
        const dest = localStorage.getItem('postLoginRedirect') || '/dashboard';
        localStorage.removeItem('postLoginRedirect');
        navigate(dest, { replace: true });
      } catch {
        // not logged in, stay on login
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{ background: '#0a0a0f' }}
    >
      {/* Indigo glow top-center */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center top, rgba(99,102,241,0.18) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}
            >
              📞
            </div>
            <span className="text-2xl font-bold text-white">CallPilot</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back</h1>
          <p className="text-gray-400 text-base">Sign in to your CallPilot dashboard</p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-8"
          style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {/* Google OAuth Button */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl font-medium text-gray-900 bg-white hover:bg-gray-50 transition-colors duration-150 mb-6"
            style={{ border: '1px solid rgba(0,0,0,0.12)', fontSize: '0.9375rem' }}
          >
            {/* Google icon SVG */}
            <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
              <path d="M47.532 24.552c0-1.636-.132-3.228-.388-4.764H24v9.02h13.204c-.572 3.064-2.308 5.66-4.916 7.404v6.148h7.948c4.656-4.292 7.296-10.616 7.296-17.808z" fill="#4285F4"/>
              <path d="M24 48c6.48 0 11.924-2.148 15.9-5.82l-7.948-6.148c-2.148 1.44-4.896 2.292-7.952 2.292-6.116 0-11.296-4.128-13.148-9.672H2.584v6.348C6.544 42.9 14.72 48 24 48z" fill="#34A853"/>
              <path d="M10.852 28.652A14.94 14.94 0 0 1 10 24c0-1.624.28-3.2.852-4.652v-6.348H2.584A23.996 23.996 0 0 0 0 24c0 3.876.928 7.548 2.584 10.8l8.268-6.148z" fill="#FBBC05"/>
              <path d="M24 9.528c3.444 0 6.532 1.184 8.96 3.508l6.716-6.716C35.92 2.38 30.476 0 24 0 14.72 0 6.544 5.1 2.584 13.2l8.268 6.148C12.704 13.656 17.884 9.528 24 9.528z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <p className="text-center text-gray-500 text-sm">
            By signing in, you agree to our{' '}
            <a href="/terms" className="text-gray-400 hover:text-white underline transition-colors">Terms</a>
            {' '}and{' '}
            <a href="/privacy" className="text-gray-400 hover:text-white underline transition-colors">Privacy Policy</a>.
          </p>
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-300 text-sm transition-colors duration-150"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
